<script lang="ts" module>
	const translations = {
		nb: {
			// Shared
			order: "Bestill",
			callMe: "Ring meg",
			orderProduct: `Bestill strømavtalen`,
			years: "år",
			genericError: "Det skjedde en feil, vennligst prøv igjen.",
			memberNumber: "Medlemsnummer",
			mobileNumber: "Mobilnummer",
			verify: "Verifiser",
			// FixedPriceValues / FixedPriceForm
			fixedPrice: (price: string) => `Fastpris: <strong>${price} øre/kWh</strong>`,
			fixedAmount: (amount: string) => `Fastbeløp: <strong>${amount} kr</strong> ${"i måneden"}`,
			bindingPeriod: (label: string) => `Bindingstid: <strong>${label}</strong>`,
			fixedPriceInfoBanner: `Denne avtalen er ikke tilgjengelig akkurat nå. Ønsker du forutsigbarhet, kan du vurdere
			strømstøtteordningen <a href="https://www.ishavskraft.no/norgespris">Norgespris</a>. Vår
			kundeservice hjelper deg gjerne.`,
			fixedPriceAreas: (area: string) => `Vi tilbyr foreløpig bare fastpris i ${area}.`,
			// StrawberryForm
			strawberryProduct: "Spotpris i Strawberry",
			memberNumberPlaceholder: "Medlemsnummer...",
			mobileNumberPlaceholder: "Mobilnummer...",
			membershipNotFound:
				"Vi fant deg dessverre ikke hos Strawberry. Husket du å bruke det mobilnummeret som er registrert for ditt medlemsnummer? Prøv gjerne på nytt, eller ta kontakt med oss i Ishavskraft.",
			membershipFoundFor: (name: string) => `Medlemskap funnet for ${name}! Bestill nedenfor.`,
			membershipFound: "Medlemskap funnet! Bestill nedenfor.",
			// BonordForm
			cardNumber: "Kortnummer",
			loadingBonord: "Henter medlemsinfo fra Bonord...",
			bonordNotFound: "Vi fant deg dessverre ikke hos Bonord.",
			bonordNotFoundDetails:
				"Har du skrevet inn riktig medlemskortnummer? Prøv gjerne på nytt, eller ta kontakt med oss i Ishavskraft på 776 12 000.",
			// CustomerSupportBanner
			mobileRequired: "Mobilnummer er påkrevd",
			mobileInvalid: "Mobilnummeret må bestå av 8 siffer, og være et gyldig norsk mobilnummer",
			thankYouCallback: (mobile: string) =>
				`Takk for henvendelsen, vi kontakter deg på ${mobile} så snart en av våre kunderådgivere blir ledig!`,
			submitError: "Noe gikk galt. Vennligst prøv igjen eller kontakt oss om problemet vedvarer.",
			yourMobileNumber: "Ditt mobilnummer",
			phone: "telefon",
			// Header
			priceNorthNorway: "Pris for Nord-Norge / Nord-Troms og Finnmark",
			outsideNorthNorway: "Utenfor Nord-Norge",
			includedInDeal: "Inkludert i avtalen",
			contractTerms: "Avtalevilkår",
			rightOfWithdrawal: "Angrerett",
			seePriceList: "Se vår prisliste",
			compareDeal: "Sammenlign strømavtalen på Forbrukerrådets strømprisportal.",
			highPriceWarning: (url: string) =>
				`Fastprisen ligger for øyeblikket over norgespris på 40 øre/kWh. Ønsker du forutsigbarhet, kan du vurdere strømstøtteordningen <a href="${url}">norgespris</a>. Vår kundeservice hjelper deg gjerne.`,
			goToCustomerService: "Gå til kundeservice",
			fixedPriceProduct: (binding: string) => `Fastpris ${binding}`,
			includedTitle: (key: string) =>
				({
					spotpris: "Spotpris",
					angrerett: "Angrerett",
					kredittvurdering: "Kredittvurdering",
					faktura: "Faktura",
					priser: "Priser",
					bindingstid: "Bindingstid",
					bruddgebyr: "Bruddgebyr",
				})[key.toLocaleLowerCase()] ?? key,
			// CustomerReviews
			customerReviewsTitle: "Hva kundene våre sier",
			favorite: "Folkefavoritten",
		},
		en: {
			// Shared
			order: "Order",
			callMe: "Call me",
			orderProduct: "Order electricity agreement",
			years: "years",
			genericError: "Something went wrong, please try again.",
			memberNumber: "Member number",
			mobileNumber: "Mobile number",
			verify: "Verify",
			// FixedPriceValues / FixedPriceForm
			fixedPrice: (price: string) => `Fixed price: <strong>${price} øre/kWh</strong>`,
			fixedAmount: (amount: string) => `Fixed amount: <strong>${amount} kr</strong> ${"per month"}`,
			bindingPeriod: (label: string) => `Binding period: <strong>${label}</strong>`,
			fixedPriceInfoBanner: `This agreement is not available right now. If you want predictability, you can consider the electricity support scheme <a href="https://www.ishavskraft.no/norgespris">Norway price</a>. Our customer service will be happy to help you.`,
			fixedPriceAreas: (area: string) => `We currently only offer fixed price in ${area}.`,
			// StrawberryForm
			strawberryProduct: "Spotpris i Strawberry",
			memberNumberPlaceholder: "Member number...",
			mobileNumberPlaceholder: "Mobile number...",
			membershipNotFound:
				"Unfortunately, we could not find you at Strawberry. Did you remember to use the mobile number registered to your member number? Please try again, or contact us at Ishavskraft.",
			membershipFoundFor: (name: string) => `Membership found for ${name}! Order below.`,
			membershipFound: "Membership found! Order below.",
			// BonordForm
			cardNumber: "Card number",
			loadingBonord: "Loading membership info from Bonord...",
			bonordNotFound: "Unfortunately, we could not find you at Bonord.",
			bonordNotFoundDetails:
				"Did you enter the correct membership card number? Please try again, or contact us at Ishavskraft at 776 12 000.",
			// CustomerSupportBanner
			mobileRequired: "Mobile number is required",
			mobileInvalid:
				"The mobile number must consist of 8 digits and be a valid Norwegian mobile number",
			thankYouCallback: (mobile: string) =>
				`Thank you for your enquiry, we will contact you at ${mobile} as soon as one of our advisors becomes available!`,
			submitError: "Something went wrong. Please try again or contact us if the problem persists.",
			yourMobileNumber: "Your mobile number",
			phone: "phone",
			// Header
			priceNorthNorway: "Price for Northern Norway / Nord-Troms and Finnmark",
			outsideNorthNorway: "Outside Northern Norway",
			includedInDeal: "Included in the deal",
			contractTerms: "Contract terms",
			rightOfWithdrawal: "Right of withdrawal",
			seePriceList: "See our price list",
			compareDeal: "Compare the deal with Forbrukerrådets electricity price portal.",
			highPriceWarning: (url: string) =>
				`The fixed price is currently above norgespris at 40 øre/kWh. If you want predictability, you can consider the electricity support scheme <a href="${url}">norgespris</a>. Our customer service will be happy to help you.`,
			goToCustomerService: "Go to customer service",
			fixedPriceProduct: (binding: string) => `Fixed price ${binding}`,
			includedTitle: (key: string) =>
				({
					spotpris: "Spot price",
					angrerett: "Right of withdrawal",
					kredittvurdering: "Credit check",
					faktura: "Invoice",
					priser: "Prices",
					bindingstid: "Binding period",
					bruddgebyr: "Cancellation fee",
				})[key.toLocaleLowerCase()] ?? key,
			// CustomerReviews
			customerReviewsTitle: "What our customers say",
			favorite: "Peoples favorite",
		},
	} satisfies Record<string, Record<string, string | ((...args: string[]) => string)>>;

	type Translations = typeof translations.nb;
	type Lang = keyof typeof translations;
	const isLang = (lang: string): lang is Lang => lang in translations;

	export const t = (lang: string): Translations =>
		isLang(lang) ? translations[lang] : translations.nb;
</script>
